import { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { he } from "date-fns/locale";
import SidePanel from "./sidePanel";
import Spinner from "./spinner";
import CUSTOMERS from "../api/services/CUSTOMERS";
import EMPLOYEES from "../api/services/EMPLOYEES";
import SERVICES from "../api/services/SERVICES";
import SCHEDULE from "../api/services/SCHEDULE";
import APPOINTMENTS from "../api/services/APPOINTMENTS";
import { calculateEndTimeOfAppointment } from "../utils/calculateEndTimeOfAppointment";
import { toast } from "react-toastify";
import styles from "../styles/_components/createAppointmentModal.module.scss";

const DAY_NAMES = {
  Sunday: "ראשון",
  Monday: "שני",
  Tuesday: "שלישי",
  Wednesday: "רביעי",
  Thursday: "חמישי",
  Friday: "שישי",
  Saturday: "שבת",
};

const CreateAppointmentModal = ({ data, setIsOpen, setSuccess, currentView }) => {
  const dropdownRef = useRef(null);

  // ── Data lists ──
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [services, setServices] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);

  // ── Client selection ──
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // ── New client form ──
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientName, setNewClientName] = useState("");
  const [newClientPhone, setNewClientPhone] = useState("");
  const [creatingClient, setCreatingClient] = useState(false);

  // ── Employee / service ──
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [businessId, setBusinessId] = useState(null);
  const [selectedServiceId, setSelectedServiceId] = useState("");

  const [timeslot, setTimeslot] = useState(
    currentView !== "month" ? format(new Date(data.start), "HH:mm") : ""
  );

  // ── Payment choice (null = not decided, 'free' = no charge, 'credit' = charge) ──
  const [paymentChoice, setPaymentChoice] = useState("free");
  const [checkingTerminal, setCheckingTerminal] = useState(false);
  const [hasTerminal, setHasTerminal] = useState(null); // null = not checked yet

  // ── Card details (shown when paymentChoice === 'credit') ──
  const [cardNumber, setCardNumber] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [cvv, setCvv] = useState("");
  const [holderId, setHolderId] = useState("");

  const [repeatPeriod, setRepeatPeriod] = useState("");
  const [expiresIn, setExpiresIn] = useState("");
  const [showRecurring, setShowRecurring] = useState(false);

  const [loading, setLoading] = useState(false);

  const user_id = JSON.parse(localStorage.getItem("user_id"));

  // ── Date display ──
  const startDate = new Date(data.start);
  const dayName = DAY_NAMES[format(startDate, "EEEE")];
  const dateNum = format(startDate, "d");
  const monthName = format(startDate, "MMMM", { locale: he });
  const timeStr = currentView !== "month" ? format(startDate, "HH:mm") : "";

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Fetch clients ──
  useEffect(() => {
    CUSTOMERS.get(undefined, undefined, "all", "")
      .then((res) => setClients(res.data))
      .catch(console.log);
  }, []);

  // ── Fetch employees ──
  useEffect(() => {
    EMPLOYEES.get(user_id)
      .then((res) => {
        const list = res.data.data;
        setEmployees(list);
        if (list.length === 1) {
          setSelectedEmployeeId(list[0].id);
          setBusinessId(list[0].businessId);
        }
      })
      .catch(console.log);
  }, []);

  // ── Fetch services when employee changes ──
  useEffect(() => {
    if (!selectedEmployeeId) return;
    SERVICES.get(selectedEmployeeId)
      .then((res) => {
        const list = res.data;
        setServices(list);
        // Auto-select if only one service exists
        if (list.length === 1) {
          setSelectedServiceId(list[0]._id);
        } else {
          setSelectedServiceId("");
        }
      })
      .catch(console.log);
  }, [selectedEmployeeId]);

  // ── Fetch time slots (month view only) ──
  useEffect(() => {
    if (currentView !== "month" || !selectedEmployeeId || !selectedServiceId) return;
    const payload = {
      employeeId: selectedEmployeeId,
      serviceId: selectedServiceId,
      date: format(startDate, "yyyy-MM-dd"),
    };
    SCHEDULE.get_timeslots(payload)
      .then((res) => {
        const unavailable = res.data.data.detailedRecords;
        const available = res.data.data.records.map((s) => ({ slot: s, available: true }));
        const merged = [...unavailable, ...available].reduce((acc, s) => {
          if (!acc.find((x) => x.slot === s.slot)) acc.push(s);
          return acc;
        }, []);
        const now = new Date();
        const isToday = startDate.toDateString() === now.toDateString();
        const filtered = isToday
          ? merged.filter((s) => {
              const [h, m] = s.slot.split(":").map(Number);
              return h * 60 + m > now.getHours() * 60 + now.getMinutes();
            })
          : merged;
        setTimeSlots(filtered);
        setTimeslot("");
      })
      .catch(console.log);
  }, [selectedEmployeeId, selectedServiceId, currentView]);

  // ── Filtered clients ──
  const filteredClients = clients.filter((c) =>
    c.user?.name?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const noResults = clientSearch.length > 0 && filteredClients.length === 0;

  // ── Create new client ──
  const handleCreateNewClient = async () => {
    if (!newClientName.trim()) {
      toast.error("יש להזין שם לקוח");
      return;
    }
    setCreatingClient(true);
    try {
      await CUSTOMERS.create_client({ name: newClientName, phone: newClientPhone });
      // Re-fetch and auto-select
      const res = await CUSTOMERS.get(undefined, undefined, "all", newClientName);
      const newClient = res.data?.[0];
      if (newClient) {
        setSelectedClient(newClient);
        setClientSearch(newClient.user?.name || newClientName);
      } else {
        setClientSearch(newClientName);
      }
      setShowNewClientForm(false);
      setNewClientName("");
      setNewClientPhone("");
      setShowClientDropdown(false);
      toast.success("לקוח נוצר בהצלחה");
    } catch (error) {
      toast.error(error.response?.data?.message || "שגיאה ביצירת לקוח");
    }
    setCreatingClient(false);
  };

  // ── Submit appointment ──
  const handleSubmit = async () => {
    if (!selectedClient || !selectedEmployeeId || !selectedServiceId) {
      toast.error("יש לבחור לקוח, עובד ושירות");
      return;
    }
    if (currentView === "month" && !timeslot) {
      toast.error("יש לבחור שעה");
      return;
    }
    if (repeatPeriod && !expiresIn) {
      toast.error("יש לבחור תקופה לתור קבוע");
      return;
    }

    // If paid service + credit charge selected → validate card fields
    if (paymentChoice === "credit" && selectedService?.price > 0) {
      if (!cardNumber || !expMonth || !expYear || !cvv) {
        toast.error("נא למלא את פרטי כרטיס האשראי");
        return;
      }
    }

    const exp = expMonth && expYear ? `${expMonth}${expYear}` : "";

    const payload = {
      employeeId: selectedEmployeeId,
      userId: selectedClient.userId,
      serviceId: selectedServiceId,
      businessId: Number(businessId),
      date: format(startDate, "yyyy-MM-dd"),
      timeSlot: timeslot,
      // cash: true bypasses credit card terminal; omit it when admin chooses to charge
      ...(paymentChoice !== "credit" && { cash: true }),
      // card details — sent only when charging credit
      ...(paymentChoice === "credit" && selectedService?.price > 0 && {
        cardNumber,
        exp,
        cvv,
        holderId,
      }),
    };
    if (repeatPeriod) {
      payload.repeatPeriod = repeatPeriod;
      payload.expiresIn = expiresIn;
    }

    setLoading(true);
    try {
      const res = await APPOINTMENTS.create(payload);
      toast.success(res.data.message);
      setIsOpen(false);
      setSuccess(true);
    } catch (error) {
      const errorMsg = error.response?.data?.message || "";
      if (errorMsg) {
        const shouldForce = window.confirm(
          `${errorMsg}\n\nהאם לקבוע בכל זאת?`
        );
        if (shouldForce) {
          try {
            const forceRes = await APPOINTMENTS.create({
              ...payload,
              isForcedBooking: true,
            });
            toast.success(forceRes.data.message);
            setIsOpen(false);
            setSuccess(true);
          } catch (e2) {
            toast.error(e2.response?.data?.message || "שגיאה בקביעת תור");
          }
        }
      } else {
        toast.error("אירעה שגיאה");
      }
    }
    setLoading(false);
  };

  const selectedService = services.find((s) => s._id === selectedServiceId);
  const endTime =
    timeslot && selectedService
      ? calculateEndTimeOfAppointment(timeslot, selectedService.time)
      : null;

  return (
    <SidePanel setIsOpen={setIsOpen} title="קביעת תור חדש">
      <div className={styles.container}>

        {/* ── Date/Time Header ── */}
        <div className={styles.header}>
          <div className={styles.dateBadge}>{dateNum}</div>
          <div className={styles.headerText}>
            <span className={styles.dayName}>יום {dayName}</span>
            <span className={styles.dateLabel}>{monthName}</span>
          </div>
          {timeStr && (
            <div className={styles.timeChip}>{timeStr}</div>
          )}
        </div>

        {/* ── Form ── */}
        <div className={styles.form}>

          {/* Client search */}
          <div className={styles.field} ref={dropdownRef}>
            <label>לקוח</label>

            {selectedClient && !showNewClientForm ? (
              <div className={styles.selectedClient}>
                <div className={styles.clientAvatar}>
                  {(selectedClient.user?.name || "?")[0].toUpperCase()}
                </div>
                <div className={styles.clientInfo}>
                  <span className={styles.clientName}>{selectedClient.user?.name}</span>
                  {selectedClient.user?.phone && (
                    <span className={styles.clientPhone}>{selectedClient.user.phone}</span>
                  )}
                </div>
                <button
                  className={styles.changeClientBtn}
                  onClick={() => {
                    setSelectedClient(null);
                    setClientSearch("");
                    setShowClientDropdown(true);
                  }}
                >
                  החלף
                </button>
              </div>
            ) : !showNewClientForm ? (
              <>
                <input
                  type="text"
                  placeholder="חיפוש לקוח..."
                  value={clientSearch}
                  onChange={(e) => {
                    setClientSearch(e.target.value);
                    setSelectedClient(null);
                    setShowClientDropdown(true);
                  }}
                  onFocus={() => setShowClientDropdown(true)}
                  className={styles.input}
                  autoFocus
                />
                {showClientDropdown && (
                  <ul className={styles.dropdown}>
                    {filteredClients.slice(0, 8).map((c) => (
                      <li
                        key={c._id}
                        onClick={() => {
                          setSelectedClient(c);
                          setClientSearch(c.user?.name || "");
                          setShowClientDropdown(false);
                        }}
                      >
                        <div className={styles.dropdownAvatar}>
                          {(c.user?.name || "?")[0].toUpperCase()}
                        </div>
                        <div className={styles.dropdownClientInfo}>
                          <span>{c.user?.name}</span>
                          {c.user?.phone && (
                            <span className={styles.phone}>{c.user.phone}</span>
                          )}
                        </div>
                      </li>
                    ))}
                    <li
                      className={styles.createClientRow}
                      onClick={() => {
                        setShowNewClientForm(true);
                        setShowClientDropdown(false);
                        setNewClientName(clientSearch);
                      }}
                    >
                      <span className={styles.plusIcon}>＋</span>
                      {noResults
                        ? `צור לקוח חדש "${clientSearch}"`
                        : "יצירת לקוח חדש"}
                    </li>
                  </ul>
                )}
              </>
            ) : null}

            {/* Inline new client form */}
            {showNewClientForm && (
              <div className={styles.newClientForm}>
                <div className={styles.newClientTitle}>לקוח חדש</div>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="שם מלא *"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  autoFocus
                />
                <input
                  className={styles.input}
                  type="tel"
                  placeholder="מספר טלפון"
                  value={newClientPhone}
                  onChange={(e) => setNewClientPhone(e.target.value)}
                />
                <div className={styles.newClientActions}>
                  <button
                    className={styles.newClientSave}
                    onClick={handleCreateNewClient}
                    disabled={creatingClient}
                  >
                    {creatingClient ? <Spinner /> : "שמור לקוח"}
                  </button>
                  <button
                    className={styles.newClientCancel}
                    onClick={() => {
                      setShowNewClientForm(false);
                      setNewClientName("");
                      setNewClientPhone("");
                    }}
                  >
                    ביטול
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Employee select */}
          {employees.length > 1 && (
            <div className={styles.field}>
              <label>עובד/ת</label>
              <select
                className={styles.select}
                value={selectedEmployeeId}
                onChange={(e) => {
                  setSelectedEmployeeId(e.target.value);
                  const emp = employees.find((em) => em.id === e.target.value);
                  if (emp) setBusinessId(emp.businessId);
                }}
              >
                <option value="" disabled>בחירת עובד/ת</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Service select */}
          <div className={styles.field}>
            <label>שירות</label>
            <select
              className={styles.select}
              value={selectedServiceId}
              onChange={(e) => setSelectedServiceId(e.target.value)}
              disabled={!selectedEmployeeId}
            >
              <option value="" disabled>בחירת שירות</option>
              {services.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name}{s.time ? ` • ${s.time} דק׳` : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Time summary (week/day view) */}
          {currentView !== "month" && selectedService && (
            <div className={styles.timeSummary}>
              <span>{timeslot}</span>
              {endTime && <><span className={styles.dash}>–</span><span>{endTime}</span></>}
              <span className={styles.duration}>({selectedService.time} דק׳)</span>
            </div>
          )}

          {/* Time slot picker (month view) */}
          {currentView === "month" && selectedEmployeeId && selectedServiceId && (
            <div className={styles.field}>
              <label>שעה</label>
              <div className={styles.slots}>
                {timeSlots.length === 0 ? (
                  <p className={styles.noSlots}>אין שעות פנויות</p>
                ) : (
                  timeSlots.map((s) => (
                    <button
                      key={s.slot}
                      className={`${styles.slot} ${timeslot === s.slot ? styles.slotActive : ""} ${!s.available ? styles.slotTaken : ""}`}
                      onClick={() => s.available && setTimeslot(s.slot)}
                      disabled={!s.available}
                    >
                      {s.slot}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ── Payment choice (shown only when service has a price) ── */}
          {selectedService && Number(selectedService.price) > 0 && (
            <div className={styles.paymentChoice}>
              <div className={styles.paymentChoiceLabel}>
                <span className={styles.paymentIcon}>💳</span>
                <span>מחיר השירות: <strong>₪{selectedService.price}</strong></span>
              </div>
              <div className={styles.paymentOptions}>
                <button
                  type="button"
                  className={`${styles.paymentOption} ${paymentChoice === "free" ? styles.paymentOptionActive : ""}`}
                  onClick={() => setPaymentChoice("free")}
                >
                  <span>🚫</span> קבע ללא חיוב
                </button>
                <button
                  type="button"
                  className={`${styles.paymentOption} ${paymentChoice === "credit" ? styles.paymentOptionCreditActive : ""}`}
                  onClick={() => setPaymentChoice("credit")}
                >
                  <span>💳</span> חייב אשראי
                </button>
              </div>
              {paymentChoice === "credit" && (
                <div className={styles.cardForm}>
                  <div className={styles.cardField}>
                    <label>מספר כרטיס *</label>
                    <input
                      type="text"
                      placeholder="XXXX XXXX XXXX XXXX"
                      maxLength={19}
                      value={cardNumber}
                      onChange={(e) => setCardNumber(e.target.value)}
                      className={styles.input}
                    />
                  </div>
                  <div className={styles.cardRow}>
                    <div className={styles.cardField}>
                      <label>תוקף</label>
                      <div className={styles.expSelects}>
                        <select
                          value={expMonth}
                          onChange={(e) => setExpMonth(e.target.value)}
                          className={styles.select}
                        >
                          <option value="">חודש</option>
                          {Array.from({ length: 12 }, (_, i) => {
                            const m = String(i + 1).padStart(2, "0");
                            return <option key={m} value={m}>{m}</option>;
                          })}
                        </select>
                        <select
                          value={expYear}
                          onChange={(e) => setExpYear(e.target.value)}
                          className={styles.select}
                        >
                          <option value="">שנה</option>
                          {Array.from({ length: 10 }, (_, i) => {
                            const y = String(new Date().getFullYear() % 100 + i).padStart(2, "0");
                            return <option key={y} value={y}>{`20${y}`}</option>;
                          })}
                        </select>
                      </div>
                    </div>
                    <div className={styles.cardField}>
                      <label>CVV *</label>
                      <input
                        type="text"
                        placeholder="XXX"
                        maxLength={4}
                        value={cvv}
                        onChange={(e) => setCvv(e.target.value)}
                        className={styles.input}
                      />
                    </div>
                  </div>
                  <div className={styles.cardField}>
                    <label>ת.ז. בעל הכרטיס</label>
                    <input
                      type="text"
                      placeholder="מספר תעודת זהות"
                      value={holderId}
                      onChange={(e) => setHolderId(e.target.value)}
                      className={styles.input}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Recurring toggle */}
          <button
            className={styles.recurringToggle}
            onClick={() => setShowRecurring((v) => !v)}
          >
            {showRecurring ? "▲" : "▼"} תור קבוע (אופציונלי)
          </button>

          {showRecurring && (
            <div className={styles.recurring}>
              <select
                className={styles.select}
                value={repeatPeriod}
                onChange={(e) => setRepeatPeriod(e.target.value)}
              >
                <option value="">ללא חזרתיות</option>
                {[
                  { label: "כל יום", value: "1d" },
                  { label: "כל יומיים", value: "2d" },
                  { label: "כל שלושה ימים", value: "3d" },
                  { label: "כל שבוע", value: "7d" },
                  { label: "כל שבועיים", value: "14d" },
                  { label: "כל 3 שבועות", value: "21d" },
                  { label: "כל חודש", value: "1m" },
                  { label: "כל חודשיים", value: "2m" },
                  { label: "כל שלושה חודשים", value: "3m" },
                  { label: "כל חצי שנה", value: "6m" },
                  { label: "כל שנה", value: "1y" },
                ].map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              {repeatPeriod && (
                <select
                  className={styles.select}
                  value={expiresIn}
                  onChange={(e) => setExpiresIn(e.target.value)}
                >
                  <option value="" disabled>בחירת תקופה</option>
                  {[
                    { label: "שבוע", value: "1w" },
                    { label: "שבועיים", value: "2w" },
                    { label: "3 שבועות", value: "3w" },
                    { label: "חודש", value: "1m" },
                    { label: "3 חודשים", value: "3m" },
                    { label: "6 חודשים", value: "6m" },
                    { label: "שנה", value: "1y" },
                  ].map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>

        {/* ── Submit ── */}
        <button className={styles.submitBtn} onClick={handleSubmit} disabled={loading || checkingTerminal}>
          {checkingTerminal ? "בודק מסוף סליקה..." : loading ? <Spinner /> : "קבע תור"}
        </button>
      </div>
    </SidePanel>
  );
};

export default CreateAppointmentModal;

// Keep named exports for backward compatibility
export const ClientCard = () => null;
export const EmployeeCard = () => null;
export const ServiceCard = () => null;
